import {
  type FC,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { COMPANY_OBJECTIVES_TAB_STRATEGIC, ICONS } from '../../consts/consts';
import type { Me, Person } from '../../types';
import type { ObjectivePerson } from '../../utils/models/Objective';

export type TryEnterPending = (newList: ObjectivePerson[]) => boolean;

function getRemovalImpact({
  oldList,
  newList,
  profilePersonId,
  isIndividual,
}: {
  oldList: ObjectivePerson[];
  newList: ObjectivePerson[];
  profilePersonId: number | undefined;
  isIndividual: boolean;
}) {
  const willPersonNotCollaboratingAnymore =
    profilePersonId !== undefined &&
    oldList.some((o) => o.id === profilePersonId) &&
    !newList.some((n) => n.id === profilePersonId);
  const willLeaveStrategicWithoutCollaborators =
    !isIndividual && oldList.length > 0 && newList.length === 0;
  return {
    willPersonNotCollaboratingAnymore,
    willLeaveStrategicWithoutCollaborators,
  };
}

const NO_IMPACT = {
  willPersonNotCollaboratingAnymore: false,
  willLeaveStrategicWithoutCollaborators: false,
};

interface Props {
  initialValue?: ObjectivePerson[];
  isOpen: boolean;
  isRootObjective: boolean;
  isIndividual: boolean;
  profilePerson?: Person;
  me?: Me;
  onConfirm: (people: ObjectivePerson[]) => void;
  onClose: () => void;
  tryEnterPendingRef: MutableRefObject<TryEnterPending | null>;
  children: ReactNode;
}

const ObjectiveCollaboratorsPendingAlert: FC<Props> = ({
  initialValue,
  isOpen,
  isRootObjective,
  isIndividual,
  profilePerson,
  me,
  onConfirm,
  onClose,
  tryEnterPendingRef,
  children,
}) => {
  const { formatMessage } = useIntl();
  const strategicPath = COMPANY_OBJECTIVES_TAB_STRATEGIC(formatMessage).path;

  const [pendingPeople, setPendingPeople] = useState<ObjectivePerson[] | null>(
    null
  );

  const profilePersonId = profilePerson?.id;
  const canWarn =
    isRootObjective && profilePersonId !== undefined && me?.id !== undefined;
  const oldList = useMemo(() => initialValue ?? [], [initialValue]);

  const {
    willPersonNotCollaboratingAnymore,
    willLeaveStrategicWithoutCollaborators,
  } =
    canWarn && pendingPeople !== null
      ? getRemovalImpact({
          oldList,
          newList: pendingPeople,
          profilePersonId,
          isIndividual,
        })
      : NO_IMPACT;
  const showWarning =
    willPersonNotCollaboratingAnymore || willLeaveStrategicWithoutCollaborators;
  const isOwnProfile = me?.id === profilePersonId;

  useEffect(() => {
    setPendingPeople(null);
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    if (pendingPeople === null) return;
    const next = pendingPeople;
    setPendingPeople(null);
    onConfirm(next);
    onClose();
  }, [pendingPeople, onConfirm, onClose]);

  const handleUndo = useCallback(() => {
    setPendingPeople(null);
  }, []);

  const tryEnterPending = useCallback<TryEnterPending>(
    (newList) => {
      if (!canWarn) return false;
      const impact = getRemovalImpact({
        oldList,
        newList,
        profilePersonId,
        isIndividual,
      });
      if (
        impact.willPersonNotCollaboratingAnymore ||
        impact.willLeaveStrategicWithoutCollaborators
      ) {
        setPendingPeople(newList);
        return true;
      }
      return false;
    },
    [canWarn, oldList, profilePersonId, isIndividual]
  );

  tryEnterPendingRef.current = tryEnterPending;

  if (!showWarning) return <>{children}</>;

  return (
    <>
      <div className="text-muted small mb-3">
        {willPersonNotCollaboratingAnymore && isOwnProfile && (
          <div>
            <FormattedMessage
              id="app.views.objectives.collaborator_removal_warning.self"
              defaultMessage="This objective will no longer appear on your profile."
            />
          </div>
        )}
        {willPersonNotCollaboratingAnymore && !isOwnProfile && (
          <div>
            <FormattedMessage
              id="app.views.objectives.collaborator_removal_warning.other"
              defaultMessage="This objective will no longer appear on {name}'s profile."
              values={{ name: profilePerson?.full_name }}
            />
          </div>
        )}
        {willLeaveStrategicWithoutCollaborators && (
          <div
            className={willPersonNotCollaboratingAnymore ? 'mt-2' : undefined}
          >
            <FormattedMessage
              id="app.views.objectives.collaborator_removal_warning.strategic_empty"
              defaultMessage="With no collaborators, it will only appear on the <link>company strategic objectives page</link>."
              values={{
                link: (chunks) => (
                  <Link
                    to={strategicPath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {chunks}
                    <i className={`${ICONS.EXTERNAL} ms-1`} />
                  </Link>
                ),
              }}
            />
          </div>
        )}
      </div>
      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={handleUndo}
        >
          <FormattedMessage
            id="app.views.widgets.inputs.objective_input.collaborators_editor.undo"
            defaultMessage="Undo"
          />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleConfirm}
        >
          <FormattedMessage
            id="app.views.widgets.inputs.objective_input.collaborators_editor.confirm"
            defaultMessage="Confirm"
          />
        </button>
      </div>
    </>
  );
};

export default ObjectiveCollaboratorsPendingAlert;
